import {useState, useRef, useEffect, useCallback} from 'react';

import {analyticsClient} from '../../../../../analytics-client';

import {useGetSharedInboxConfig} from '../../graphql/hooks/use-get-shared-inbox-config';
import {useSendSharedInboxVerificationEmail} from '../../graphql/hooks/use-send-shared-inbox-verification-email';

import {POLL_INTERVAL_MS, VerificationState, VERIFICATION_TIMEOUT_MS} from './consts';

interface UseForwardingVerificationResult {
    verificationState: VerificationState;
    handleSendTestEmail: () => Promise<void>;
    isSending: boolean;
}

export function useForwardingVerification(
    sharedEmailAddress: string | undefined,
    forwardingVerifiedTime: number | null | undefined
): UseForwardingVerificationResult {
    const {sendVerificationEmail, isLoading: isSending} = useSendSharedInboxVerificationEmail();

    const [verificationState, setVerificationState] = useState<VerificationState>(
        VerificationState.IDLE
    );
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const verifiedTimeAtSendRef = useRef<number | null | undefined>(null);

    const isPolling = verificationState === VerificationState.VERIFYING;
    const {refetch} = useGetSharedInboxConfig({
        pollInterval: isPolling ? POLL_INTERVAL_MS : 0,
    });

    useEffect(() => {
        if (
            isPolling &&
            forwardingVerifiedTime &&
            forwardingVerifiedTime !== verifiedTimeAtSendRef.current
        ) {
            setVerificationState(VerificationState.VERIFIED);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        }
    }, [forwardingVerifiedTime, isPolling]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleSendTestEmail = useCallback(async () => {
        if (!sharedEmailAddress) {
            return;
        }

        analyticsClient.track(analyticsClient.EVENTS.TRACK_SHARED_EMAIL_INBOX_TEST_ATTEMPTED);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Remember the current verified time so we can detect when it changes
        verifiedTimeAtSendRef.current = forwardingVerifiedTime;

        await sendVerificationEmail(sharedEmailAddress);
        await refetch();
        setVerificationState(VerificationState.VERIFYING);

        timeoutRef.current = setTimeout(() => {
            setVerificationState(VerificationState.FAILED);
            timeoutRef.current = null;
        }, VERIFICATION_TIMEOUT_MS);
    }, [sharedEmailAddress, forwardingVerifiedTime, sendVerificationEmail, refetch]);

    return {
        verificationState,
        handleSendTestEmail,
        isSending,
    };
}
